// Vendor
import {
  useMemo,
  useState,
  useRef,
  memo,
  type HTMLAttributes,
  type ReactNode,
  type ReactEventHandler,
  useEffect,
} from 'react';
import classNames from 'classnames';
import 'lazysizes';
import 'lazysizes/plugins/attrchange/ls.attrchange';

import * as styles from './ProgressiveImage.css';

const SRCSET_SIZES = [64, 128, 256, 512, 1024, 2048];

export interface ProgressiveImageProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'onLoad'> {
  src: string;
  alt: string;
  className?: string | null;
  imageWrapperClassName?: string | null;
  overlay?: ReactNode | null;
  overlayClassName?: string | null;
  isTransparent?: boolean;
  shouldCoverContainer?: boolean;
  shouldFillWidth?: boolean | null;
  shouldFillHeight?: boolean;
  isDraggable?: boolean;
  onLoad?: ReactEventHandler<HTMLImageElement> | null;
}

/**
 * An image component which uses lazy loading and auto-sizing to ensure that images are delivered more optimally
 *
 * @class ProgressiveImage
 *
 * @param   {string}          src                           Desktop/default image url
 * @param   {string}          alt                           Alt text for image
 * @param   {string}          [className]                   Class name to apply to image container
 * @param   {string}          [imageWrapperclassName]       Class name to apply to wrapper element for the image
 * @param   {bool}            [shouldCoverContainer=false]  Whether the image should expand to cover its parent container
 * @param   {bool}            [isTransparent=false]         Whether image has transparency and should not have a grey background
 * @param   {func}            [onLoad]                      Callback function when image is fully loaded
 * @param   {element}         [overlay]                     Element for an icon to overlay over the image
 * @param   {string}          [overlayClassName]            Class name to apply to the overlay wrapper
 * @param   {bool}            [shouldFillWidth=false]       Whether we should force the image to fill the width of its container,
 *                                                            regardless of the aspect ratio of the container or image
 * @param   {bool}            [shouldFillHeight=false]      Whether we should force the image to fill the height of its container,
 *                                                            regardless of the aspect ratio of the container or image
 * @param   {bool}            [isDraggable=false]           Whether we should allow the image to be dragged. We usually want this disabled
 *                                                            because it's not very useful to users and it can interfere with drag and drop file upload UIs
 */
const ProgressiveImage = ({
  src = '',
  alt,
  className = null,
  imageWrapperClassName = null,
  shouldCoverContainer = false,
  isTransparent = false,
  onLoad = null,
  overlay = null,
  overlayClassName = null,
  shouldFillWidth = false,
  shouldFillHeight = false,
  isDraggable = false,
  ...props
}: ProgressiveImageProps) => {
  // Keep a ref to the outermost containing div or the image so we can measure
  // the parent node that this image is being rendered in
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const [dimensions, setDimensions] = useState<null | {
    width: number;
    height: number;
  }>(null);
  const imageAspectRatio = useMemo(
    () => (dimensions ? dimensions.width / dimensions.height : 1),
    [dimensions],
  );

  // How the image component should be sized relative to the parent
  const [imageComponentSizes, setImageComponentSizes] = useState({
    width: 'auto',
    height: 'auto',
  });

  useEffect(() => {
    // Calculate the width and height that the image should use relative to the container
    // based on its aspect ratio
    // Doing this in `useEffect` so we can guarantee that the component has mounted and we will be able to
    // to access the ref to the container
    const imageContainer = imageContainerRef.current;
    if (!imageContainer) {
      return;
    }

    const containerBoundingRect = imageContainer.getBoundingClientRect();

    const containerAspectRatio = containerBoundingRect.width / containerBoundingRect.height;

    if (shouldCoverContainer || imageAspectRatio === containerAspectRatio) {
      // if we want the image to fully cover its container or its aspect ratio exactly matches the ratio of its container,
      // let's expand to fill both the width and height
      setImageComponentSizes({
        width: '100%',
        height: '100%',
      });
    } else if (imageAspectRatio >= containerAspectRatio || shouldFillWidth) {
      // If the image aspect ratio is wider or as wide as the parent node or we are being forced to fill width,
      // let's expand to fill the parent's width
      setImageComponentSizes({
        width: '100%',
        height: 'auto',
      });
    } else if (imageAspectRatio <= containerAspectRatio || shouldFillHeight) {
      // If the image aspect ratio is taller or as tall as the parent nodeor we are being forced to fill height,
      // let's expand to fill the parent's height
      setImageComponentSizes({
        width: 'auto',
        height: '100%',
      });
    } else {
      setImageComponentSizes({
        width: 'auto',
        height: 'auto',
      });
    }
  }, [imageAspectRatio, shouldCoverContainer, shouldFillHeight, shouldFillWidth]);

  const srcSet = useMemo(() => {
    try {
      const parsedSrc = new URL(src);
      if (parsedSrc.hostname.endsWith('imgix.net')) {
        // Add imgix params to the image URL
        parsedSrc.searchParams.set('auto', 'compress,format');
        parsedSrc.searchParams.set('fit', 'max');

        // Generate a srcset using the `w` imgix width param
        return SRCSET_SIZES.map((size) => {
          const srcWithSize = new URL(parsedSrc);
          srcWithSize.searchParams.set('w', size.toString());
          return `${srcWithSize.toString()} ${size}w`;
        }).join(',');
      }

      // If we don't know how to apply CDN params to the image URL, just return the original URL
      return parsedSrc.toString();
    } catch (e) {
      console.error("Error parsing 'src' prop passed to ProgressiveImage:", e);
    }

    // If we failed to parse the src, return null; it's not a valid image URL
    return null;
  }, [src]);

  if (!srcSet) {
    return null;
  }

  return (
    <div
      className={classNames(styles.ProgressiveImageContainer, className)}
      {...styles.dataShouldCoverContainer.props(shouldCoverContainer)}
      {...styles.dataIsTransparent.props(isTransparent)}
      ref={imageContainerRef}
      // Pass through any other props to the progressive image container
      {...props}
    >
      <div
        className={classNames(styles.ImageWrapper, imageWrapperClassName)}
        style={imageComponentSizes}
      >
        <img
          key={src}
          // lazysizes will calculate the displayed size of the image
          // and automatically apply the appropriate source from `data-srcset`
          data-sizes="auto"
          data-srcset={srcSet}
          className={classNames(
            styles.Image,
            // This is a magical class which will indicate to `lazysizes` that this image
            // should be lazy loaded and sized when it gets near/enters the viewport
            'lazyload',
          )}
          alt={alt}
          onLoad={(event) => {
            setDimensions({
              width: event.currentTarget.naturalWidth,
              height: event.currentTarget.naturalHeight,
            });
            onLoad?.(event);
          }}
          style={imageComponentSizes}
          draggable={isDraggable}
          crossOrigin="anonymous"
        />
        {overlay && (
          <div className={classNames(styles.OverlayIcon, overlayClassName)}>
            {/* Render overlay icon on top of image if one was provided */}
            {overlay}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(ProgressiveImage);
